import {
  EmployeeStatusEnum,
  RequestCollection as RequestCollectionDto,
  RequestResponse as RequestDto,
  RequestTypeCategoryChampionV2,
  RequestTypeResponse as RequestTypeDto,
} from '@electricjs/core_entity-client';

import {
  employeeIdsWithCategoryChampionRole,
  employeeIdsWithOpenTasks,
  offboardingScenarioEmployeesById,
  pendingMockEmployees,
  simpleEmployee,
} from './employeeData';
import { SLACK_SUB_RESOURCE_APP_ID } from './groupChangeSubResourceData';

// A due date relative to today, so the dashboard's overdue tasks tile always has
// overdue and due-this-week tasks to show (due dates are calendar dates).
const dueDateFromToday = (days: number) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T00:00:00Z`;
};

// One in-progress task assigned to each scenario employee flagged with open
// tasks (other than simpleEmployee, which already has richer task data
// hard-coded below). Lets the offboarding ReassignTasks step surface for
// those employees without bespoke per-employee request fixtures.
const scenarioOpenTasks: RequestDto[] = Array.from(employeeIdsWithOpenTasks)
  .filter(id => id !== simpleEmployee.id)
  .map((employeeId, index) => ({
    id: `scenario-task-${employeeId}`,
    request_type_id: '000b2ff8-97dc-4b29-b7d1-7ed65a9ea001',
    organization_id: 'string',
    requester_user_id: '27b852f0-54dc-453e-9eb5-03e8bb437c54',
    on_behalf_of: employeeId,
    assigned_to: employeeId,
    status: 'IN_PROGRESS',
    data: { application_key: 'zoom' },
    automation_status: 'NOT_CONNECTED',
    created_at: `2026-05-${String(index + 1).padStart(2, '0')}T00:00:00Z`,
  }));

// An employee id that no mock employee list contains.
export const RESTRICTED_CHAMPION_EMPLOYEE_ID =
  'restricted-champion-employee-id';

// A Support task is rendered by the fallback task details mapping, so any slug
// without a dedicated mapping works.
const SUPPORT_REQUEST_TYPE_ID = '000b2ff8-97dc-4b29-b7d1-7ed65a9ea007';

export const requests: RequestDto[] = [
  {
    id: '002df5c0-054f-11ee-be56-0242ac120005',
    request_type_id: '000b2ff8-97dc-4b29-b7d1-7ed65a9ea001',
    organization_id: 'string',
    requester_user_id: '27b852f0-54dc-453e-9eb5-03e8bb437c54',
    on_behalf_of: 'simple-employee-id',
    status: 'COMPLETED',
    request_collection_id: '002df5c0-054f-11ee-be56-0242ac120005',
    data: {
      application_key: 'zoom',
    },
    automation_status: 'SUCCESS',
  },
  {
    id: '002df5c0-054f-11ee-be56-0242ac12000b',
    due_date: dueDateFromToday(-3),
    request_type_id: '000b2ff8-97dc-4b29-b7d1-7ed65a9ea001',
    organization_id: 'string',
    requester_user_id: '27b852f0-54dc-453e-9eb5-03e8bb437c54',
    status: 'IN_PROGRESS',
    on_behalf_of: 'simple-employee-id',
    assigned_to: 'simple-employee-id',
    request_collection_id: '002df5c0-054f-11ee-be56-0242ac120005',
    data: {
      application_key: 'zoom',
    },
    resolved_at: '',
    resolved_by_user_id: '',
    automation_status: 'NOT_CONNECTED',
  },
  {
    id: '002df5c0-054f-11ee-be56-0242ac120013',
    due_date: dueDateFromToday(0),
    request_type_id: '000b2ff8-97dc-4b29-b7d1-7ed65a9ea006',
    organization_id: 'string',
    requester_user_id: '27b852f0-54dc-453e-9eb5-03e8bb437c54',
    status: 'IN_PROGRESS',
    on_behalf_of: 'simple-employee-id',
    request_collection_id: '002df5c0-054f-11ee-be56-0242ac120005',
    data: {
      application_key: 'zoom',
    },
    resolved_at: '',
    resolved_by_user_id: '',
    automation_status: 'NOT_CONNECTED',
  },
  {
    id: '002df5c0-054f-11ee-be56-0242ac12000d',
    due_date: dueDateFromToday(-1),
    request_type_id: '000b2ff8-97dc-4b29-b7d1-7ed65a9ea002',
    organization_id: 'string',
    requester_user_id: '27b852f0-54dc-453e-9eb5-03e8bb437c54',
    status: 'IN_PROGRESS',
    on_behalf_of: 'simple-employee-id',
    assigned_to: 'simple-employee-id',
    request_collection_id: '002df5c0-054f-11ee-be56-0242ac120005',
    data: {
      application_key: '1password',
    },
    resolved_at: '',
    resolved_by_user_id: '',
    automation_status: 'NOT_ENABLED',
  },
  {
    id: '002df5c0-054f-11ee-be56-0242ac12000e',
    request_type_id: '000b2ff8-97dc-4b29-b7d1-7ed65a9ea001',
    organization_id: 'string',
    requester_user_id: '27b852f0-54dc-453e-9eb5-03e8bb437c54',
    status: 'IN_PROGRESS',
    on_behalf_of: 'simple-employee-id',
    request_collection_id: '002df5c0-054f-11ee-be56-0242ac120005',
    data: {
      application_key: 'zoom',
    },
    resolved_at: '',
    resolved_by_user_id: '',
    automation_status: 'SCHEDULED',
    not_before: '2026-05-01T00:00:00Z',
  },
  {
    id: '002df5c0-054f-11ee-be56-0242ac120011',
    due_date: dueDateFromToday(0),
    request_type_id: '000b2ff8-97dc-4b29-b7d1-7ed65a9ea004',
    organization_id: 'string',
    requester_user_id: '27b852f0-54dc-453e-9eb5-03e8bb437c54',
    system_requester_id: 'sr-mock-1',
    on_behalf_of: 'simple-employee-id',
    assigned_to: 'simple-employee-id',
    status: 'IN_PROGRESS',
    data: {},
    automation_status: 'NOT_CONNECTED',
    created_at: '2026-05-01T00:00:00Z',
  },
  {
    id: '002df5c0-054f-11ee-be56-0242ac120012',
    request_type_id: '000b2ff8-97dc-4b29-b7d1-7ed65a9ea005',
    organization_id: 'string',
    requester_user_id: '27b852f0-54dc-453e-9eb5-03e8bb437c54',
    system_requester_id: 'sr-mock-1',
    on_behalf_of: 'simple-employee-id',
    assigned_to: 'simple-employee-id',
    status: 'IN_PROGRESS',
    // No due date: overdue only because it has expired
    data: { expires_at: dueDateFromToday(-2) },
    automation_status: 'NOT_CONNECTED',
    created_at: '2026-05-01T00:00:00Z',
  },
  {
    id: '002df5c0-054f-11ee-be56-0242ac120010',
    request_type_id: '000b2ff8-97dc-4b29-b7d1-7ed65a9ea003',
    organization_id: 'string',
    requester_user_id: '27b852f0-54dc-453e-9eb5-03e8bb437c54',
    status: 'IN_PROGRESS',
    on_behalf_of: 'simple-employee-id',
    created_at: '2026-05-01T00:00:00Z',
    due_date: '2026-05-01T00:00:00Z',
    data: {
      source: 'hris_sync',
      groups_added: ['103c6fce-a69d-459f-b4d1-4499b162cebf'],
      groups_removed: ['7c82f409-f60e-485a-9679-caec45799903'],
      group_change_impact: {
        apps_gained: [
          {
            application_id: 'app-uuid-1',
            group_ids: ['103c6fce-a69d-459f-b4d1-4499b162cebf'],
          },
        ],
        apps_lost: [
          {
            application_id: SLACK_SUB_RESOURCE_APP_ID,
            group_ids: ['7c82f409-f60e-485a-9679-caec45799903'],
          },
        ],
        config_deltas: [],
      },
    },
    automation_status: 'NOT_CONNECTED',
  },
  {
    // A failed automation on a connected integration, so the retry action shows.
    id: '002df5c0-054f-11ee-be56-0242ac120015',
    request_type_id: '000b2ff8-97dc-4b29-b7d1-7ed65a9ea001',
    organization_id: 'string',
    requester_user_id: '27b852f0-54dc-453e-9eb5-03e8bb437c54',
    on_behalf_of: 'simple-employee-id',
    assigned_to: 'simple-employee-id',
    status: 'IN_PROGRESS',
    data: { application_key: 'slack' },
    automation_status: 'ERROR',
    automation_error_code: 'common.internal_error',
    created_at: '2026-05-01T00:00:00Z',
  },
  {
    // A Support task whose requester is outside the viewer's divisions, so the
    // task details page shows Restricted.
    id: '002df5c0-054f-11ee-be56-0242ac120016',
    request_type_id: SUPPORT_REQUEST_TYPE_ID,
    organization_id: 'string',
    requester_user_id: '27b852f0-54dc-453e-9eb5-03e8bb437c54',
    on_behalf_of: RESTRICTED_CHAMPION_EMPLOYEE_ID,
    assigned_to: 'simple-employee-id',
    status: 'IN_PROGRESS',
    data: { issue: { name: 'Laptop' } },
    automation_status: 'NOT_ENABLED',
    created_at: '2026-05-01T00:00:00Z',
  },
  ...scenarioOpenTasks,
  // An open IT setup for the first pending employee: their row on the
  // dashboard's "Pending employees" tile and the Pending tab shows
  // "Start IT setup" (the others show "Start onboard")
  {
    id: 'pending-it-setup-request-1',
    request_type_id: '000b2ff8-97dc-4b29-b7d1-7ed65a9ea004',
    organization_id: 'string',
    requester_user_id: '27b852f0-54dc-453e-9eb5-03e8bb437c54',
    system_requester_id: 'sr-mock-1',
    on_behalf_of: pendingMockEmployees[0].id,
    // The task page shows its error state for a request with no assignee
    assigned_to: 'simple-employee-id',
    status: 'IN_PROGRESS',
    data: {},
    automation_status: 'NOT_CONNECTED',
    created_at: '2026-05-01T00:00:00Z',
  },
];

export const simpleRequestCollection: RequestCollectionDto = {
  id: '002df5c0-054f-11ee-be56-0242ac120005',
  created_at: '2021-09-30T15:18:24.000Z',
  completed_at: '2021-09-30T15:18:24.000Z',
};

export const requestTypes: RequestTypeDto[] = [
  {
    id: '000b2ff8-97dc-4b29-b7d1-7ed65a9ea001',
    name: 'Create Account',
    slug: 'create_account',
    request_type_category_id: 'a5bb2ff8-97dc-4b29-b7d1-7ed65a9ea926',
    is_internal: true,
  },
  {
    id: '000b2ff8-97dc-4b29-b7d1-7ed65a9ea002',
    name: 'Remove Account',
    slug: 'remove_account',
    request_type_category_id: 'a5bb2ff8-97dc-4b29-b7d1-7ed65a9ea926',
    is_internal: true,
  },
  {
    id: '000b2ff8-97dc-4b29-b7d1-7ed65a9ea003',
    name: 'Group Change Review',
    slug: 'group_change_review',
    request_type_category_id: 'a5bb2ff8-97dc-4b29-b7d1-7ed65a9ea926',
    is_internal: true,
  },
  {
    id: '000b2ff8-97dc-4b29-b7d1-7ed65a9ea006',
    name: 'Get Access or License',
    slug: 'get_access_or_license',
    request_type_category_id: 'a5bb2ff8-97dc-4b29-b7d1-7ed65a9ea926',
    is_internal: true,
  },
  {
    id: '000b2ff8-97dc-4b29-b7d1-7ed65a9ea004',
    name: 'HRIS Onboarding',
    slug: 'hris_onboarding',
    request_type_category_id: 'cat-onboarding',
    is_internal: true,
  },
  {
    id: '000b2ff8-97dc-4b29-b7d1-7ed65a9ea005',
    name: 'HRIS Offboarding',
    slug: 'hris_offboarding',
    request_type_category_id: 'cat-offboarding',
    is_internal: true,
  },
  {
    id: SUPPORT_REQUEST_TYPE_ID,
    name: 'Support',
    slug: 'support',
    request_type_category_id: 'a5bb2ff8-97dc-4b29-b7d1-7ed65a9ea926',
    is_internal: false,
  },
];

// Champion entries for the Applications category, derived from the scenario
// fixtures flagged with `hasCategoryChampionRole`. Lets the offboarding
// ChampionReassignment step surface for those employees without bespoke
// per-employee champion fixtures.
const scenarioApplicationsCategoryChampions = Array.from(
  employeeIdsWithCategoryChampionRole
).map(employeeId => {
  const employee = offboardingScenarioEmployeesById[employeeId];
  return {
    id: `category-champion-applications-${employeeId}`,
    employee_id: employeeId,
    first_name: employee?.first_name ?? '',
    last_name: employee?.last_name ?? '',
    email: employee?.email ?? '',
  };
});

export const requestTypeCategories = [
  {
    id: 'a5bb2ff8-97dc-4b29-b7d1-7ed65a9ea926',
    name: 'Applications',
    slug: 'applications',
    description: 'Application requests',
    // Every scenario employee flagged with hasCategoryChampionRole is a
    // primary champion here so the offboarding ChampionReassignment step has
    // a category to surface for them.
    champions: scenarioApplicationsCategoryChampions,
    is_support_center_tile: true,
  },
  {
    id: 'cat-device',
    name: 'Device',
    slug: 'device',
    description: 'Device requests',
    // The champion's employee is absent from the mock directory and employee
    // map (it is outside the user's divisions), so the UI shows Restricted.
    champions: [
      {
        id: 'category-champion-device-1',
        employee_id: RESTRICTED_CHAMPION_EMPLOYEE_ID,
      },
    ],
    is_support_center_tile: false,
  },
  {
    id: 'cat-onboarding',
    name: 'Onboarding',
    slug: 'onboarding',
    description: 'Onboarding requests',
    // John as secondary champion (rank derived from order); a second
    // champion is here to keep him from being the only one for this category.
    champions: [
      {
        id: 'category-champion-onboarding-1',
        employee_id: 'other-champion-employee-id',
        first_name: 'Jane',
        last_name: 'Roe',
        email: 'jane.roe@simple-org.com',
      },
      {
        id: 'category-champion-onboarding-2',
        employee_id: 'simple-employee-id',
        first_name: 'John',
        last_name: 'Doe',
        email: 'simple-user@simple-org.com',
      },
    ],
    is_support_center_tile: false,
  },
  {
    id: 'cat-offboarding',
    name: 'Offboarding',
    slug: 'offboarding',
    description: 'Offboarding requests',
    // Control category — not championed by John, should NOT appear in his
    // offboarding flow.
    champions: [
      {
        id: 'category-champion-offboarding-1',
        employee_id: 'other-champion-employee-id',
        first_name: 'Jane',
        last_name: 'Roe',
        email: 'jane.roe@simple-org.com',
      },
    ],
    is_support_center_tile: false,
  },
];

// v2 view of the champions above, flat across categories like the real list.
// The embedded v1 champion id doubles as the assignment id; rank is the order.
export const requestTypeCategoryChampionsV2: RequestTypeCategoryChampionV2[] =
  requestTypeCategories.flatMap(category =>
    category.champions.map((champion, rank) => ({
      id: champion.id,
      organization_id: 'string',
      request_type_category_id: category.id,
      rank,
      champion: {
        id: `champion-${champion.employee_id}`,
        employee_id: champion.employee_id,
        status: EmployeeStatusEnum.Active,
      },
    }))
  );

export const systemRequesters = [
  {
    id: 'sr-mock-1',
    name: 'Mock HRIS',
    created_at: '2026-05-01T00:00:00Z',
    updated_at: '2026-05-01T00:00:00Z',
  },
];
